import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileText, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRecordSearch } from "../../../hooks/useRecordSearch";
import styles from "./LinkedRecordsSection.module.css";
import { sanitizeText, sanitizeURL } from "../../../../../utils/sanitize";
import useLinkedRecordPreview from "../../../hooks/useLinkedRecordPreview";

function evaluateExternalUrlInput(
  input,
  { requireHttps = true, sanitizer = sanitizeURL } = {}
) {
  if (typeof input !== "string") {
    return { url: null, errorKey: null };
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return { url: null, errorKey: null };
  }

  const hasProtocol = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(trimmed);
  const containsDot = trimmed.includes(".");
  const mightBeUrl = hasProtocol || containsDot;

  if (/\s/.test(trimmed)) {
    if (!mightBeUrl) {
      return { url: null, errorKey: null };
    }
    return { url: null, errorKey: "records.editor.links.errors.no_spaces" };
  }

  const candidate = hasProtocol ? trimmed : `https://${trimmed}`;

  let sanitized;
  try {
    sanitized = sanitizer(candidate);
  } catch {
    return { url: null, errorKey: "records.editor.links.errors.invalid" };
  }

  if (!sanitized) {
    if (requireHttps && candidate.startsWith("http://")) {
      return { url: null, errorKey: "records.editor.links.errors.https_only" };
    }
    return { url: null, errorKey: "records.editor.links.errors.invalid" };
  }

  let url;
  try {
    url = new URL(sanitized);
  } catch {
    return { url: null, errorKey: "records.editor.links.errors.invalid" };
  }

  if (!url.host) {
    return { url: null, errorKey: "records.editor.links.errors.hostname_required" };
  }
  if (requireHttps && url.protocol !== "https:") {
    return { url: null, errorKey: "records.editor.links.errors.https_only" };
  }
  if (!hasProtocol && !url.host.includes(".")) {
    return { url: null, errorKey: "records.editor.links.errors.domain_required" };
  }

  return { url: url.toString(), errorKey: null };
}

function normalizeExternalUrl(input, options) {
  return evaluateExternalUrlInput(input, options).url;
}

function looksLikeUrl(s) {
  if (!s) return false;
  const t = s.trim();
  return /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(t) || t.includes(".") || t.startsWith("www.");
}

function getExternalDisplayHost(url) {
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

const deriveOverlayRecords = (overlay) => {
  if (!overlay) {
    return undefined;
  }

  if (overlay.isRemoval) {
    return [];
  }

  if (!Array.isArray(overlay.value)) {
    console.warn("AI links overlay has invalid value:", overlay.value);
    return [];
  }

  return overlay.value
    .map((entry, index) => {
      if (!entry || typeof entry !== "object") {
        console.warn("Skipping AI links overlay entry:", entry);
        return null;
      }

      const targetId =
        typeof entry.targetId === "string" ? entry.targetId.trim() : "";
      const targetType =
        entry.targetType === "record" || entry.targetType === "external"
          ? entry.targetType
          : null;

      if (!targetId || !targetType) {
        console.warn(
          "Skipping AI links overlay entry missing targetId/targetType:",
          entry,
          "at index",
          index
        );
        return null;
      }

      const preview = {
        targetId,
        targetType,
      };

      if (
        typeof entry.targetExcerpt === "string" &&
        entry.targetExcerpt.trim()
      ) {
        preview.targetExcerpt = entry.targetExcerpt.trim();
      }

      if (
        entry.targetAuthor &&
        typeof entry.targetAuthor === "object" &&
        (typeof entry.targetAuthor.name === "string" ||
          typeof entry.targetAuthor.avatar === "string")
      ) {
        const author = {};
        if (
          typeof entry.targetAuthor.name === "string" &&
          entry.targetAuthor.name.trim()
        ) {
          author.name = entry.targetAuthor.name.trim();
        }
        if (
          typeof entry.targetAuthor.avatar === "string" &&
          entry.targetAuthor.avatar.trim()
        ) {
          author.avatar = entry.targetAuthor.avatar.trim();
        }
        if (Object.keys(author).length > 0) {
          preview.targetAuthor = author;
        }
      }

      return preview;
    })
    .filter(Boolean);
};

function LinkedRecordListItem({ link, onRemove = () => {}, isReadOnly }) {
  const { t } = useTranslation();
  const { title, excerpt, status, fallbackTitle } = useLinkedRecordPreview(link);

  const isExternal = link.targetType === "external";
  const IconComponent = isExternal ? Globe : FileText;
  const displayTitle = sanitizeText(title);

  const metaLabel = isExternal
    ? sanitizeText(
        link.targetExcerpt ||
          fallbackTitle ||
          getExternalDisplayHost(link.targetId)
      )
    : sanitizeText(
        excerpt ||
          link.targetExcerpt ||
          (status === "denied"
            ? t("records.detail.linked_records.denied_title", "No permission")
            : status === "loading"
              ? t("records.detail.linked_records.loading_excerpt", "Fetching link details...")
              : status === "error"
                ? t("records.detail.linked_records.error_excerpt", "Unable to load link")
                : t("records.editor.links.record_fallback", "Record"))
      );

  const ariaTitle = useMemo(() => {
    if (title) return title;
    if (link.targetType === "external") return fallbackTitle;
    if (status === "denied") {
      return t("records.detail.linked_records.denied_title", "No permission");
    }
    if (status === "loading") {
      return t("records.detail.linked_records.loading_title", "Loading linked record...");
    }
    if (status === "error") {
      return t("records.detail.linked_records.error_title", "Unable to load link");
    }
    return t("records.detail.linked_records.open_record", "Open linked record");
  }, [title, link.targetType, fallbackTitle, status, t]);

  return (
    <div
      data-testid="linked-record-item"
      className={`${styles.linkedRecord} ${
        isExternal ? styles.external : ""
      } ${isReadOnly ? styles.overlayValue : ""}`}
    >
      <div className={`${styles.icon} ${isExternal ? styles.iconExternal : styles.iconInternal}`}>
        <IconComponent size={18} strokeWidth={1.75} aria-hidden="true" />
      </div>
      <div className={styles.content}>
        <div className={styles.title}>{displayTitle}</div>
        <div className={styles.meta}>{metaLabel}</div>
      </div>
      {!isReadOnly && (
        <button
          className={styles.removeButton}
          onClick={onRemove}
          aria-label={t("records.editor.links.remove_link_aria", {
            title: ariaTitle,
            defaultValue: `Remove link to ${ariaTitle}`,
          })}
          type="button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default function LinkedRecordsSection({
  records,
  onUpdate,
  currentRecordId,
  aiOverlay,
}) {
  const { t, i18n } = useTranslation();
  const baseRecords = useMemo(
    () => (Array.isArray(records) ? records : []),
    [records]
  );
  const [hasFocus, setHasFocus] = useState(false);
  const { query, setQuery, results, isLoading, error, isShowingRecent, loadRecentRecords } =
    useRecordSearch(currentRecordId);

  const overlayRecords = useMemo(
    () => deriveOverlayRecords(aiOverlay),
    [aiOverlay]
  );
  const isOverlayActive = Boolean(aiOverlay);
  const isRemovalOverlay = Boolean(aiOverlay?.isRemoval);
  const displayedRecords = useMemo(() => {
    if (!isOverlayActive) {
      return baseRecords;
    }

    if (isRemovalOverlay) {
      return [];
    }

    return overlayRecords ?? [];
  }, [isOverlayActive, isRemovalOverlay, baseRecords, overlayRecords]);

  const bannerCopy = useMemo(() => {
    if (!isOverlayActive) {
      return null;
    }

    if (isRemovalOverlay) {
      return t("records.editor.links.ai.remove_all", "AI will remove all linked records.");
    }

    const count = (overlayRecords ?? []).length;
    if (count === 0) {
      return t(
        "records.editor.links.ai.replace_empty",
        "AI suggestion will replace linked records with an empty list."
      );
    }

    return t("records.editor.links.ai.replace_count", {
      count,
      defaultValue:
        count === 1
          ? "AI will replace linked records with 1 link."
          : `AI will replace linked records with ${count} links.`,
    });
  }, [isOverlayActive, isRemovalOverlay, overlayRecords, t]);

  useEffect(() => {
    if (isOverlayActive) {
      setHasFocus(false);
      setQuery("");
    }
  }, [isOverlayActive, setQuery]);

  const { url: normalizedExternalUrl, errorKey: externalUrlErrorKey } = useMemo(() => {
    if (!looksLikeUrl(query)) {
      return { url: null, errorKey: null };
    }
    return evaluateExternalUrlInput(query);
  }, [query]);
  const isExternalAlreadyLinked = useMemo(() => {
    if (!normalizedExternalUrl) return false;
    return baseRecords.some(
      (r) =>
        r.targetType === "external" &&
        normalizeExternalUrl(r.targetId, { requireHttps: false }) === normalizedExternalUrl
    );
  }, [baseRecords, normalizedExternalUrl]);
  const externalDisplayHost = useMemo(
    () => (normalizedExternalUrl ? sanitizeText(getExternalDisplayHost(normalizedExternalUrl)) : ""),
    [normalizedExternalUrl]
  );
  const externalDisplayUrl = useMemo(
    () => (normalizedExternalUrl ? sanitizeText(normalizedExternalUrl) : ""),
    [normalizedExternalUrl]
  );
  const resultsCount = results.length;
  const shouldShowDropdown = !isOverlayActive && hasFocus && (
    isLoading ||
    error ||
    resultsCount > 0 ||
    normalizedExternalUrl ||
    externalUrlErrorKey ||
    (!isLoading && !error && query.trim())
  );
  const describedByIds = [];
  if (error) describedByIds.push("search-error");
  if (externalUrlErrorKey) describedByIds.push("url-error");
  const inputAriaDescribedBy = describedByIds.length ? describedByIds.join(" ") : undefined;

  const handleAddRecord = useCallback(
    (record) => {
      if (isOverlayActive) {
        return;
      }

      const currentRecords = Array.isArray(records) ? records : [];

      // Check if already linked
      if (currentRecords.some((r) => r.targetId === record.id)) {
        return; // Already linked, skip
      }

      const newLink = {
        targetId: record.id,
        targetType: "record",
        addedAt: new Date(),
      };

      onUpdate([...currentRecords, newLink]);
      setQuery("");
    },
    [isOverlayActive, records, onUpdate, setQuery]
  );

  const handleRemoveRecord = useCallback(
    (index) => {
      if (isOverlayActive) {
        return;
      }

      const newRecords = Array.isArray(records) ? [...records] : [];
      newRecords.splice(index, 1);
      onUpdate(newRecords);
    },
    [isOverlayActive, records, onUpdate]
  );

  const handleFocus = useCallback(() => {
    if (isOverlayActive) {
      return;
    }
    setHasFocus(true);
    loadRecentRecords(); // Load recent records when focused
  }, [isOverlayActive, loadRecentRecords]);

  const handleBlur = useCallback(() => {
    // Add a small delay to allow clicks on suggestions
    setTimeout(() => {
      setHasFocus(false);
    }, 200);
  }, []);

  const handleAddExternalLink = useCallback(() => {
    if (isOverlayActive) {
      return;
    }

    if (!normalizedExternalUrl || isExternalAlreadyLinked) {
      return;
    }

    const newLink = {
      targetId: normalizedExternalUrl,
      targetType: "external",
      addedAt: new Date(),
    };

    const currentRecords = Array.isArray(records) ? records : [];
    onUpdate([...currentRecords, newLink]);
    setQuery("");
  }, [
    isOverlayActive,
    normalizedExternalUrl,
    isExternalAlreadyLinked,
    onUpdate,
    records,
    setQuery,
  ]);

  const handleInputKeyDown = useCallback(
    (event) => {
      if (isOverlayActive) {
        return;
      }

      if (
        event.key === "Enter" &&
        normalizedExternalUrl &&
        !isLoading &&
        !error &&
        resultsCount === 0
      ) {
        event.preventDefault();
        handleAddExternalLink();
      }
    },
    [
      isOverlayActive,
      normalizedExternalUrl,
      isLoading,
      error,
      resultsCount,
      handleAddExternalLink,
    ]
  );

  return (
    <div className={styles.section}>
      <label className={styles.label}>
        {t("records.editor.links.label", "Linked Records")}
      </label>

      <div
        className={`${styles.container} ${
          isOverlayActive ? styles.overlayActive : ""
        }`}
      >
        {bannerCopy && (
          <div
            className={`${styles.overlayBanner} ${
              isRemovalOverlay ? styles.overlayBannerRemoval : ""
            }`}
            aria-live="polite"
          >
            {bannerCopy}
          </div>
        )}

        {displayedRecords.length > 0 && (
          <div
            className={`${styles.list} ${
              isOverlayActive ? styles.listOverlay : ""
            }`}
          >
            {displayedRecords.map((record, index) => (
              <LinkedRecordListItem
                key={`${record.targetId}-${index}`}
                link={record}
                onRemove={
                  isOverlayActive ? undefined : () => handleRemoveRecord(index)
                }
                isReadOnly={isOverlayActive}
              />
            ))}
          </div>
        )}

        <div className={styles.inputContainer}>
          <svg
            className={styles.inputIcon}
            viewBox="0 0 24 24"
            width="18"
            height="18"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <input
            type="text"
            className={`${styles.input} ${
              isOverlayActive ? styles.inputReadOnly : ""
            }`}
            placeholder={t(
              "records.editor.links.search_placeholder",
              "Search your records or paste a link to add..."
            )}
            value={query}
            onChange={(e) => {
              if (!isOverlayActive) {
                setQuery(e.target.value);
              }
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleInputKeyDown}
            aria-label={t(
              "records.editor.links.search_aria",
              "Search records to link"
            )}
            aria-describedby={inputAriaDescribedBy}
            readOnly={isOverlayActive}
            aria-readonly={isOverlayActive || undefined}
          />

          <div
            className={`${styles.suggestions} ${shouldShowDropdown ? styles.active : ""}`}
          >
            {isLoading && (
              <div className={styles.loading}>
                <span className={styles.spinner}></span>
                {t("records.editor.links.searching", "Searching...")}
              </div>
            )}

            {error && (
              <div id="search-error" className={styles.error}>
                {t(
                  "records.editor.links.errors.search_failed",
                  "Failed to search records. Please try again."
                )}
              </div>
            )}

            {externalUrlErrorKey && (
              <div id="url-error" className={styles.error}>
                {t(externalUrlErrorKey)}
              </div>
            )}

            {normalizedExternalUrl && (
              <button
                className={`${styles.suggestion} ${styles.external} ${
                  isExternalAlreadyLinked ? styles.disabled : ""
                }`}
                onClick={() => !isExternalAlreadyLinked && handleAddExternalLink()}
                type="button"
                disabled={isExternalAlreadyLinked}
              >
                <div className={`${styles.suggestionIcon} ${styles.suggestionIconExternal}`}>
                  <Globe size={18} strokeWidth={1.75} aria-hidden="true" />
                </div>
                <div className={styles.suggestionContent}>
                  <div className={styles.suggestionTitle}>
                    {t("records.editor.links.add_external", {
                      host: externalDisplayHost || externalDisplayUrl,
                      defaultValue: `Add link to ${externalDisplayHost || externalDisplayUrl}`,
                    })}
                  </div>
                  <div className={styles.suggestionMeta}>{externalDisplayUrl}</div>
                </div>
                {isExternalAlreadyLinked && (
                  <span className={styles.linkedBadge}>
                    {t("records.editor.links.linked_badge", "Linked")}
                  </span>
                )}
              </button>
            )}

            {!isLoading && !error && !externalUrlErrorKey && results.length === 0 && query && (
              <div className={styles.empty}>
                {t("records.editor.links.no_records_matching", {
                  query,
                  defaultValue: `No records found matching "${query}"`,
                })}
              </div>
            )}

            {!isLoading && !error && isShowingRecent && results.length > 0 && (
              <div className={styles.sectionLabel}>
                {t("records.editor.links.recent_section", "Recent Records")}
              </div>
            )}

            {!isLoading &&
              !error &&
              results.map((record) => {
                const isLinked = baseRecords.some((r) => r.targetId === record.id);
                const updatedAtRaw =
                  record.metadata?.updatedAt?.toDate?.() || record.metadata?.updatedAt;
                const updatedAtDate = new Date(updatedAtRaw);
                const formattedUpdatedDate = Number.isFinite(updatedAtDate.getTime())
                  ? new Intl.DateTimeFormat(
                      i18n?.resolvedLanguage || i18n?.language
                    ).format(updatedAtDate)
                  : t("common.unknown_date", "Unknown date");

                return (
                  <button
                    key={record.id}
                    className={`${styles.suggestion} ${
                      isLinked ? styles.disabled : ""
                    }`}
                    onClick={() => !isLinked && handleAddRecord(record)}
                    type="button"
                    disabled={isLinked}
                  >
                    <div className={`${styles.suggestionIcon} ${styles.suggestionIconInternal}`}>
                      <FileText size={18} strokeWidth={1.75} aria-hidden="true" />
                    </div>
                    <div className={styles.suggestionContent}>
                      <div className={styles.suggestionTitle}>
                        {record.content?.title ||
                          t("records.views.note.card.untitled", "Untitled")}
                      </div>
                      <div className={styles.suggestionMeta}>
                        {record.content?.tags
                          ?.slice(0, 3)
                          .map((tag) => `#${tag}`)
                          .join(" ") ||
                          t("records.editor.links.updated_at", {
                            date: formattedUpdatedDate,
                            defaultValue: `Updated ${new Date(
                              record.metadata?.updatedAt?.toDate?.() ||
                                record.metadata?.updatedAt
                            ).toLocaleDateString()}`,
                          })}
                      </div>
                    </div>
                    {isLinked && (
                      <span className={styles.linkedBadge}>
                        {t("records.editor.links.linked_badge", "Linked")}
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}
